import AppPagination from "@app/common/components/AppPagination";
import { Template } from "@app/common/components/Template";
import { IFiltersPagination } from "@app/common/types/http";
import { formattedDataSelect, formatLastUpdate } from "@app/common/utils/utils";
import { GCidAccount } from "@app/config/globalsConst";
import routes from "@app/config/routes";

import { useGetCompanyClientList, useGetContractList } from "@app/services/common/hooks";
import { useGetSupportParamsList } from "@app/services/supportParams/hooks";
import { SupportParamsFilter } from "@app/services/supportParams/types";
import { ActionIcon, Button, Grid, Menu, Select, Table, Tooltip } from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import {
  IconCirclePlus,
  IconCopy,
  IconDetails,
  IconDotsVertical,
  IconPencil
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "@app/common/hooks/usePermissions";
import {
  RBAC_ACTIONS,
  RBAC_LABELS,
  MODULE_PERMISSION_PREFIX
} from "@app/common/utils/permissionsConstants";

const SupportsParameters = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canCreate = can(RBAC_ACTIONS.CREATE, MODULE_PERMISSION_PREFIX.TM_CONTRACT_SUPPORTS);
  const canEdit = can(RBAC_ACTIONS.EDIT, MODULE_PERMISSION_PREFIX.TM_CONTRACT_SUPPORTS);
  const [filters, setFilters] = useDebouncedState<IFiltersPagination & SupportParamsFilter>(
    { contractId: null, corporateClient: null },
    500
  );

  const {
    data: supportList = {
      results: [],
      rowTotal: 0
    }
  } = useGetSupportParamsList({
    ...filters,
    perpage: 10
  });

  const {
    data: companyList = {
      results: [],
      rowTotal: 0
    }
  } = useGetCompanyClientList({
    status: "enabled",
    eaccount: GCidAccount()
  });

  const {
    data: contractList = {
      results: [],
      rowTotal: 0
    }
  } = useGetContractList(
    {
      corporateClient: Number(filters.corporateClient),
      slim: 1
    },
    filters.corporateClient !== null
  );

  const FiltersHeader = () => {
    return (
      <Grid align="flex-end" mb="lg" w="100%" data-testid="supportsparameters-filters">
        <Grid.Col span={3}>
          <Select
            data-testid="supportsparameters-company-select"
            label="Empresa / Cliente"
            data={formattedDataSelect(companyList.results, "name", "id")}
            searchable
            placeholder="Seleccionar..."
            onChange={(value) => {
              setFilters((state) => ({
                ...state,
                corporateClient: value ?? "",
                contractId: ""
              }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <Select
            data-testid="supportsparameters-contract-select"
            label="Contrato"
            searchable
            data={formattedDataSelect(contractList.results, "contractName", "id")}
            placeholder="Seleccionar..."
            value={filters.contractId}
            onChange={(value) => {
              setFilters((state) => ({
                ...state,
                contractId: value ?? ""
              }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={6} className="flex justify-end">
          <Tooltip
            label={canCreate ? "Crear parametrización" : RBAC_LABELS.NO_PERMISSION}
            withArrow
          >
            <Button
              data-testid="supportsparameters-create-button"
              variant="subtle"
              disabled={!canCreate}
              className={!canCreate ? "noPermissionDisabled" : ""}
              rightSection={<IconCirclePlus />}
              onClick={() => {
                if (!canCreate) {
                  return;
                }
                navigate(routes.getCreateSupportParameter, {
                  state: { action: "create" }
                });
              }}
            >
              Crear parametrización
            </Button>
          </Tooltip>
        </Grid.Col>
      </Grid>
    );
  };

  const renderRow = (option: (typeof supportList.results)[number], index: number) => {
    const date = (option?.supports.at(0)?.createdAt ?? "").split("T")[0].replaceAll("/", "-");
    return (
      <Table.Tr key={option?.corporateClientId} data-testid={`supportsparameters-row-${index}`}>
        <Table.Td>{option?.corporateClientName}</Table.Td>

        <Table.Td>{option?.contractName}</Table.Td>

        <Table.Td ta={"center"}>{option?.supports?.length}</Table.Td>
        <Table.Td ta={"start"}>{option?.supports.at(0)?.createdBy}</Table.Td>
        <Table.Td ta={"center"}>{date}</Table.Td>
        <Table.Td ta="start" style={{ minWidth: "260px" }}>
          {formatLastUpdate(option?.updatedAt, option?.updatedBy)}
        </Table.Td>
        <Table.Td ta="end">
          <Menu position="bottom-end" withArrow data-testid={`supportsparameters-menu-${index}`}>
            <Menu.Target>
              <ActionIcon variant="subtle" data-testid={`supportsparameters-menu-button-${index}`}>
                <IconDotsVertical />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                data-testid={`supportsparameters-edit-item-${index}`}
                disabled={!canEdit}
                leftSection={<IconPencil />}
                onClick={() =>
                  navigate(routes.getEditSupportParameter, {
                    state: {
                      action: "edit",
                      data: {
                        contractId: option.contractId,
                        corporateClient: option.corporateClientId
                      }
                    }
                  })
                }
              >
                {!canEdit ? RBAC_LABELS.NO_PERMISSION : "Editar"}
              </Menu.Item>
              <Menu.Item
                data-testid={`supportsparameters-duplicate-item-${index}`}
                leftSection={<IconCopy />}
                onClick={() => alert("En desarrollo")}
              >
                Duplicar
              </Menu.Item>
              <Menu.Item
                data-testid={`supportsparameters-detail-item-${index}`}
                leftSection={<IconDetails />}
                onClick={() =>
                  navigate(routes.getEditSupportParameter, {
                    state: {
                      action: "detail",
                      data: {
                        contractId: option.contractId,
                        corporateClient: option.corporateClientId
                      }
                    }
                  })
                }
              >
                Ver detalle
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Table.Td>
      </Table.Tr>
    );
  };

  const renderTable = () => {
    return (
      <Table className="biowel-table" data-testid="supportsparameters-table">
        <Table.Thead>
          <Table.Tr>
            <Table.Th ta={"start"}>Empresa/Cliente</Table.Th>
            <Table.Th>Contrato</Table.Th>
            <Table.Th ta={"center"}>Documentos</Table.Th>
            <Table.Th ta={"start"}>Registrado por</Table.Th>
            <Table.Th ta={"center"}>Fecha</Table.Th>
            <Table.Th ta="start" style={{ minWidth: "260px" }}>
              Última Actualización
            </Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {supportList?.results?.length ? (
            supportList?.results?.map((option, index) => renderRow(option, index))
          ) : (
            <Table.Tr>
              <Table.Td ta="center" colSpan={12} data-testid="supportsparameters-no-data">
                No hay información para mostrar.
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    );
  };

  return (
    <Template title="Parametrización de soportes" data-testid="supportsparameters-template">
      {FiltersHeader()}
      {renderTable()}

      <AppPagination
        data-testid="supportsparameters-pagination"
        total={supportList?.rowTotal || 0}
        value={filters.page || 1}
        onChange={(value) => setFilters((state) => ({ ...state, page: value }))}
      />
    </Template>
  );
};

export default SupportsParameters;
